import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { countryCodes } from '../utils/countryCodes';
import { 
  UserPlus, Mail, Phone, Lock, Eye, EyeOff, 
  CheckCircle, Sparkles, ChefHat, Shield, 
  Award, Gift, Star, ArrowRight, Users,
  PiggyBank, Truck, Clock, Heart, Crown,
  Calendar, MapPin, PhoneCall, ShieldCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function SignupPage({ isMobile, setCurrentPage, onLogin }) {
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+263',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [particles, setParticles] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(countryCodes.find(c => c.code === '+263'));

  // Create floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      speed: Math.random() * 2 + 0.5,
      color: i % 4 === 0 ? '#fbbf24' : i % 4 === 1 ? '#f97316' : i % 4 === 2 ? '#dc2626' : '#84cc16'
    }));
    setParticles(newParticles);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSignupData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    setSignupData(prev => ({ ...prev, countryCode: code }));
    const country = countryCodes.find(c => c.code === code);
    setSelectedCountry(country);
  };

  const validateStep = (stepNumber) => {
    const errors = {};
    
    if (stepNumber === 1) {
      if (!signupData.name.trim()) errors.name = 'Name is required';
      if (!signupData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(signupData.email)) {
        errors.email = 'Invalid email format';
      }
      if (!signupData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^\d{9,}$/.test(signupData.phone.replace(/\D/g, ''))) {
        errors.phone = 'Invalid phone number';
      }
    }
    
    if (stepNumber === 2) {
      if (!signupData.password) {
        errors.password = 'Password is required';
      } else if (signupData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (!signupData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (signupData.password !== signupData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(2)) return;
    if (!signupData.acceptTerms) {
      alert('Please accept the terms and conditions');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "signUp",
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone,
        countryCode: signupData.countryCode,
        password: signupData.password
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        
        // Success animation
        setTimeout(() => {
          onLogin(userData);
        }, 1000);
      } else {
        alert(response.data.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "googleSignIn",
        googleId: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        imageUrl: decoded.picture
      });

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        onLogin(userData);
      } else {
        alert('Google signup failed. Please try again.');
      }
    } catch (error) {
      console.error('Google signup error:', error);
      alert('Google signup failed. Please try again.');
    }
  };

  const benefits = [
    { icon: <PiggyBank />, title: "Best Prices", desc: "Wholesale rates for members" },
    { icon: <Truck />, title: "Free Delivery", desc: "On orders above $50" },
    { icon: <Clock />, title: "Priority Access", desc: "Early access to new stock" },
    { icon: <Gift />, title: "Exclusive Offers", desc: "Members-only discounts" },
    { icon: <Heart />, title: "Quality Guarantee", desc: "100% satisfaction or refund" },
    { icon: <Crown />, title: "VIP Status", desc: "Premium customer service" }
  ];

  const steps = [
    { number: 1, title: "Personal Info", icon: <UserPlus /> },
    { number: 2, title: "Security", icon: <Shield /> },
    { number: 3, title: "Complete", icon: <CheckCircle /> }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-black to-amber-900/20">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient Mesh */}
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-900/10 via-transparent to-red-900/5"></div>
        
        {/* Floating Particles */}
        <AnimatePresence>
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
                opacity: 0.6,
              }}
              animate={{
                y: ['0vh', '-100vh'],
                x: [0, Math.sin(particle.id) * 50],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 15 / particle.speed,
                repeat: Infinity,
                ease: "linear",
                delay: particle.id * 0.1,
              }}
            />
          ))}
        </AnimatePresence>

        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-to-r from-amber-500/20 to-orange-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-r from-red-500/15 to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-3/4 left-1/3 w-[400px] h-[400px] bg-gradient-to-r from-green-500/10 to-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>

        {/* Animated Lines */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"
            style={{
              top: `${i * 20}%`,
              left: 0,
              right: 0,
            }}
            animate={{
              opacity: [0.1, 0.5, 0.1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.3,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Left Side - Benefits & Features */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2"
          >
            {/* Premium Badge */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="inline-flex items-center space-x-3 mb-8"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 p-4 rounded-2xl">
                  <Crown className="w-8 h-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-amber-300 via-orange-300 to-yellow-200 bg-clip-text text-transparent">
                  Join The Family
                </h1>
                <p className="text-amber-400 font-bold">Premium Member Benefits Await</p>
              </div>
            </motion.div>

            {/* Welcome Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
                Become A
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
                  VIP Member
                </span>
              </h2>
              <p className="text-gray-300 text-lg md:text-xl">
                Unlock exclusive benefits and premium quality meats. Join 15,000+ satisfied customers.
              </p>
            </motion.div>

            {/* Benefits Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 gap-4 mb-8"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl blur group-hover:blur-xl transition-all duration-300"></div>
                  <div className="relative bg-gradient-to-b from-white/10 to-transparent backdrop-blur-sm border border-white/10 rounded-xl p-4 group-hover:border-amber-500/30 transition-all duration-300">
                    <div className="flex items-start space-x-3">
                      <div className="text-amber-400 mt-1">{benefit.icon}</div>
                      <div>
                        <h4 className="text-white font-bold">{benefit.title}</h4>
                        <p className="text-gray-400 text-sm">{benefit.desc}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-3 gap-6"
            >
              {[
                { value: '15K+', label: 'Members', icon: <Users className="w-4 h-4" /> },
                { value: '4.9★', label: 'Rating', icon: <Star className="w-4 h-4" /> },
                { value: '24/7', label: 'Support', icon: <PhoneCall className="w-4 h-4" /> },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center space-x-2 mb-2">
                    <div className="text-amber-400">{stat.icon}</div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                  </div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 pt-6 border-t border-gray-800"
            >
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-gray-400">Secure Signup</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-400">Est. 2009</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-400">Chitungwiza Based</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Registration Form */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: 90 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:w-1/2 max-w-lg"
          >
            {/* Glowing Registration Card */}
            <div className="relative group">
              {/* Animated Glow Border */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-500 to-emerald-500 rounded-3xl blur opacity-30 group-hover:opacity-70 transition duration-1000 group-hover:duration-200 animate-gradient"></div>
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-gray-900/90 via-black/80 to-gray-900/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                {/* Progress Steps */}
                <motion.div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    {steps.map((s, index) => (
                      <div key={s.number} className="flex items-center">
                        <motion.div
                          animate={{ 
                            scale: step >= s.number ? 1.2 : 1,
                            backgroundColor: step >= s.number ? '#f59e0b' : '#374151'
                          }}
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                            step >= s.number ? 'border-amber-500' : 'border-gray-700'
                          } transition-all duration-300`}
                        >
                          <span className={`font-bold ${
                            step >= s.number ? 'text-white' : 'text-gray-400'
                          }`}>
                            {s.number}
                          </span>
                        </motion.div>
                        {index < steps.length - 1 && (
                          <div className={`w-16 h-1 mx-2 ${
                            step > s.number ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gray-700'
                          } transition-all duration-300`}></div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {steps[step - 1].title}
                    </h2>
                    <p className="text-gray-400">Step {step} of 3</p>
                  </div>
                </motion.div>

                {/* Registration Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <AnimatePresence mode="wait">
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className="space-y-6"
                      >
                        {/* Name Input */}
                        <motion.div whileHover={{ scale: 1.02 }} className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                            <UserPlus className="w-5 h-5" />
                          </div>
                          <input
                            type="text"
                            name="name"
                            value={signupData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                            placeholder="Your full name"
                          />
                          {formErrors.name && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {formErrors.name}
                            </motion.p>
                          )}
                        </motion.div>

                        {/* Email Input */}
                        <motion.div whileHover={{ scale: 1.02 }} className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                            <Mail className="w-5 h-5" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={signupData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                            placeholder="your@email.com"
                          />
                          {formErrors.email && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {formErrors.email}
                            </motion.p>
                          )}
                        </motion.div>

                        {/* Phone Input */}
                        <motion.div whileHover={{ scale: 1.02 }} className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                            <Phone className="w-5 h-5" />
                          </div>
                          <div className="flex gap-2">
                            <div className="relative">
                              <select 
                                name="countryCode"
                                value={signupData.countryCode}
                                onChange={handleCountryChange}
                                className="appearance-none pl-10 pr-8 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-all duration-300"
                              >
                                {countryCodes.map((country) => (
                                  <option key={country.code} value={country.code}>
                                    {country.flag} {country.code}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                                ▼
                              </div>
                            </div>
                            <input
                              type="tel"
                              name="phone"
                              value={signupData.phone}
                              onChange={handleInputChange}
                              className="flex-1 pl-4 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                              placeholder="77 123 4567"
                            />
                          </div>
                          {formErrors.phone && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {formErrors.phone}
                            </motion.p>
                          )}
                        </motion.div>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className="space-y-6"
                      >
                        {/* Password Input */}
                        <motion.div whileHover={{ scale: 1.02 }} className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                            <Lock className="w-5 h-5" />
                          </div>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={signupData.password}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-12 pr-12 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                            placeholder="Create strong password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-500 transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                          {formErrors.password && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {formErrors.password}
                            </motion.p>
                          )}
                        </motion.div>

                        {/* Confirm Password Input */}
                        <motion.div whileHover={{ scale: 1.02 }} className="relative">
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                            <Shield className="w-5 h-5" />
                          </div>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={signupData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-12 pr-12 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-500 transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                          {formErrors.confirmPassword && (
                            <motion.p 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {formErrors.confirmPassword}
                            </motion.p>
                          )}
                        </motion.div>

                        {/* Password Strength Indicator */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Password Strength:</span>
                            <span className={`font-bold ${
                              signupData.password.length >= 8 ? 'text-green-500' : 'text-yellow-500'
                            }`}>
                              {signupData.password.length >= 8 ? 'Strong' : 'Weak'}
                            </span>
                          </div>
                          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full bg-gradient-to-r from-amber-500 to-green-500"
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(signupData.password.length * 8.33, 100)}%` }}
                              transition={{ duration: 0.3 }}
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            Must be at least 6 characters with letters and numbers
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="space-y-6"
                      >
                        {/* Terms and Conditions */}
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-6 rounded-xl border border-amber-500/20"
                        >
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              name="acceptTerms"
                              checked={signupData.acceptTerms}
                              onChange={handleInputChange}
                              className="mt-1 h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-600 rounded bg-gray-900"
                              id="terms"
                            />
                            <label htmlFor="terms" className="text-gray-300">
                              I agree to the{' '}
                              <button 
                                type="button"
                                className="text-amber-400 hover:text-amber-300 font-semibold"
                                onClick={() => alert('Terms and conditions will be displayed here')}
                              >
                                Terms & Conditions
                              </button>{' '}
                              and acknowledge that my data will be processed in accordance with the{' '}
                              <button 
                                type="button"
                                className="text-amber-400 hover:text-amber-300 font-semibold"
                                onClick={() => alert('Privacy policy will be displayed here')}
                              >
                                Privacy Policy
                              </button>
                            </label>
                          </div>
                        </motion.div>

                        {/* Welcome Message */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                          className="text-center p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20"
                        >
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                          <h3 className="text-xl font-bold text-white mb-2">Ready to Join!</h3>
                          <p className="text-gray-300">
                            Complete your registration to unlock premium benefits
                          </p>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between pt-6 border-t border-gray-800">
                    {step > 1 && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={prevStep}
                        className="px-6 py-3 border-2 border-gray-700 text-gray-300 rounded-xl font-bold hover:border-amber-500 hover:text-amber-400 transition-all duration-300 flex items-center space-x-2"
                      >
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        <span>Back</span>
                      </motion.button>
                    )}
                    
                    {step < 3 ? (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={nextStep}
                        className="ml-auto px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all duration-300 flex items-center space-x-2"
                      >
                        <span>Continue</span>
                        <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="submit"
                        disabled={isLoading || !signupData.acceptTerms}
                        className="ml-auto px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center space-x-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Creating Account...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Complete Signup</span>
                          </>
                        )}
                      </motion.button>
                    )}
                  </div>
                </form>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700/50"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-gray-900 text-gray-400 text-sm font-medium">
                      QUICK SIGNUP
                    </span>
                  </div>
                </div>

                {/* Google Signup */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex justify-center"
                >
                  <GoogleLogin
                    clientId={GOOGLE_CLIENT_ID}
                    onSuccess={handleGoogleLogin}
                    onError={() => {
                      alert('Google signup failed. Please try again.');
                    }}
                    useOneTap={false}
                    theme="filled_black"
                    size="large"
                    shape="rectangular"
                    text="signup_with"
                  />
                </motion.div>

                {/* Footer Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 text-center space-y-4"
                >
                  <p className="text-gray-400">
                    Already have an account?{' '}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setCurrentPage('login')}
                      className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400 font-bold hover:from-amber-300 hover:to-orange-300 transition-all duration-300"
                    >
                      Sign In Instead →
                    </motion.button>
                  </p>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    onClick={() => setCurrentPage('home')}
                    className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                    <span>Back to Homepage</span>
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating Decorative Elements */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-20 right-20 hidden lg:block"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full blur-xl opacity-30"></div>
          <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 p-4 rounded-full">
            <ChefHat className="w-8 h-8 text-white" />
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute bottom-20 left-20 hidden lg:block"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-xl opacity-30"></div>
          <div className="relative bg-gradient-to-br from-green-600 to-emerald-600 p-4 rounded-full">
            <Award className="w-8 h-8 text-white" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default SignupPage;